<template>
  <div class="lj-garage-panel">
    <div class="garage-toolbar">
      <el-input
        v-model="garageVehicleSearchText"
        placeholder="车牌号码"
        clearable
        prefix-icon="el-icon-search"
        :disabled="disabled"
        class="lj-garage-search"
      />
      <button
        type="button"
        class="lj-garage-icon-btn"
        title="刷新"
        :disabled="disabled"
        @click="loadGarageTreeData"
      >
        <i class="el-icon-refresh" />
      </button>
      <button
        type="button"
        v-if="canManageVehicles" class="lj-garage-icon-btn lj-garage-icon-btn--settings"
        title="车辆管理"
        :disabled="disabled"
        @click="goVehicleManage"
      >
        <i class="el-icon-setting" />
      </button>
    </div>
    <div class="vehicle-tree-wrap" @click="onGarageTreeWrapClick">
      <el-tree
        ref="garageVehicleTree"
        :data="displayVehicleTreeData"
        :show-checkbox="showCheckbox"
        node-key="id"
        highlight-current
        default-expand-all
        :props="{ children: 'children', label: 'label', isLeaf: 'isLeaf', disabled: 'disabled' }"
        :render-content="renderGarageTreeContent"
        class="vehicle-tree"
        @node-click="onGarageTreeNodeClick"
        @check="onGarageTreeCheck"
        @check-change="onGarageTreeCheckChange"
      />
    </div>
  </div>
</template>

<script>
import { authState, hasPermission } from '@/utils/auth'
import { isLicensePlateFormatValid } from '@/utils/searchFilterValidate'
import {
  GARAGE_NODE_LOAD_MORE,
  applyElTreeExpandAll,
  buildGroupLabel,
  collectGarageVehicleNodes,
  createGarageLoadMoreNode,
  filterGarageTreeByQuery,
  findGarageNodeById,
  isGarageGroupNode,
  loadNormalVehicleGarageTree,
  resolveGarageGroupExpectedChildTotal,
  renderGarageVehicleTreeIcon,
  resolveGarageVehicleColor,
  shouldShowGarageLoadMore
} from '@/utils/garageTree'

/** 与 LeftJoinBicycleMultiGarage 车库接口一致（仅本面板使用，不修改该文件） */
export default {
  name: 'LeftJoinGaragePanel',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    /** 为 false 时不请求车库树（例如左侧「车库」页签未展示） */
    enableTreeFetch: {
      type: Boolean,
      default: true
    },
    /**
     * 非空且 garageTreeListUseRequest 为 true 时，首层车库树用 request.get 请求该路径（如独立页 本地正常车辆候选）。
     * 默认空则仍走 request.get('本地正常车辆候选')（带 Authorization）。
     */
    garageTreeListUrl: {
      type: String,
      default: ''
    },
    garageTreeListUseRequest: {
      type: Boolean,
      default: false
    },
    /**
     * 非空且 garageTreeChildrenUseRequest 为 true 时，懒加载子节点用 request.get（参数 parentId、page、pageSize 与原先一致）。
     * 默认空则仍走 request.get('本地候选子节点', …)（带 Authorization）。
     */
    garageTreeChildrenUrl: {
      type: String,
      default: ''
    },
    garageTreeChildrenUseRequest: {
      type: Boolean,
      default: false
    },
    showCheckbox: {
      type: Boolean,
      default: false
    },
    /** 车牌 → 查询结果后缀，如 [48km/h 行8分14秒 郫都区] */
    vehicleStatusMap: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      garageLoadMoreLoading: false,
      suppressGarageTreeCheckChange: false,
      garageVehicleSearchText: '',
      vehicleTreeData: [],
      selectedGarageNode: null,
      requestSequence: 0
    }
  },
  computed: {
    canManageVehicles() { return hasPermission('vehicle:list') },
    candidateScope() {
      return authState.user && hasPermission('vehicle:list') ? String(authState.user.id) : ''
    },
    displayVehicleTreeData() {
      const q = this.garageVehicleSearchText != null ? String(this.garageVehicleSearchText).trim() : ''
      if (!q) return this.vehicleTreeData
      return filterGarageTreeByQuery(this.vehicleTreeData, q)
    }
  },
  mounted() {
    this.tryLoadGarageTreeFromProp()
  },
  beforeDestroy() {
    this.requestSequence++
  },
  watch: {
    enableTreeFetch(val) {
      if (val) this.tryLoadGarageTreeFromProp()
      else {
        this.requestSequence++
        this.vehicleTreeData = []
      }
    },
    candidateScope() {
      this.requestSequence++
      this.vehicleTreeData = []
      this.selectedGarageNode = null
      this.tryLoadGarageTreeFromProp()
    },
    garageVehicleSearchText() {
      this.syncGarageSearchExpandState()
      this.tryLoadGarageTreeFromProp()
    },
    displayVehicleTreeData() {
      this.syncGarageSearchExpandState()
    },
    vehicleStatusMap: {
      deep: true,
      handler() {
        this.refreshTreeDisplay()
      }
    }
  },
  methods: {
    tryLoadGarageTreeFromProp() {
      if (!this.enableTreeFetch) return
      this.loadGarageTreeData()
    },
    goVehicleManage() {
      if (!this.canManageVehicles) return
      this.$router.push('/vehicles').catch(() => {})
    },
    emitApplyVehicleFromNode(data) {
      if (!data || data.vehicleNo == null) return
      const plate = String(data.vehicleNo).trim()
      if (!plate) return
      if (!isLicensePlateFormatValid(plate)) {
        this.$message.warning(`车牌格式不正确：${plate}`)
        return
      }
      const vco = resolveGarageVehicleColor(data)
      this.$emit('apply-vehicle', { vclN: plate, vco })
    },
    onGarageTreeNodeClick(data) {
      if (!data) {
        this.selectedGarageNode = null
        return
      }
      if (data.type === GARAGE_NODE_LOAD_MORE) {
        void this.handleGarageLoadMoreClick(data)
        return
      }
      this.selectedGarageNode = data
      if (data && data.vehicleNo != null) {
        this.$nextTick(() => {
          const tree = this.$refs.garageVehicleTree
          if (tree && typeof tree.setCurrentKey === 'function') {
            tree.setCurrentKey(data.id)
          }
        })
        this.emitApplyVehicleFromNode(data)
      }
    },
    onGarageTreeCheckChange() {
      if (this.suppressGarageTreeCheckChange) return
      // 勾选/取消统一由 @check 向父组件派发，避免重复触发
    },
    resolveGarageTreeNodeChecked(data, checkedInfo) {
      let checked = false
      if (typeof checkedInfo === 'boolean') {
        checked = checkedInfo
      } else if (checkedInfo && typeof checkedInfo === 'object') {
        const idStr = data && data.id != null ? String(data.id) : ''
        const keys = checkedInfo.checkedKeys
        if (Array.isArray(keys) && idStr) {
          checked = keys.map((k) => String(k)).includes(idStr)
        } else {
          const checkedNodes = Array.isArray(checkedInfo.checkedNodes) ? checkedInfo.checkedNodes : []
          checked = checkedNodes.some((n) => n && n.id != null && String(n.id) === idStr)
        }
      }
      if (!checked && this.$refs.garageVehicleTree && data && data.id != null) {
        const tree = this.$refs.garageVehicleTree
        if (typeof tree.getCheckedKeys === 'function') {
          checked = tree.getCheckedKeys().map((k) => String(k)).includes(String(data.id))
        }
      }
      return checked
    },
    async onGarageTreeCheck(data, checkedInfo) {
      if (!this.showCheckbox || this.suppressGarageTreeCheckChange) return
      if (!data || data.type === GARAGE_NODE_LOAD_MORE) return

      if (data.vehicleNo != null) {
        const plate = String(data.vehicleNo || '').trim()
        if (!plate) return
        const checked = this.resolveGarageTreeNodeChecked(data, checkedInfo)
        this.$emit('vehicle-check-change', {
          checked,
          vehicles: [{ node: data, vclN: plate, vco: resolveGarageVehicleColor(data) }]
        })
        return
      }

      if (!isGarageGroupNode(data)) return

      this.suppressGarageTreeCheckChange = true
      const checked = this.resolveGarageTreeNodeChecked(data, checkedInfo)
      try {
        await this.ensureGarageGroupDescendantsLoaded(data)
        const vehicles = collectGarageVehicleNodes([data]).map((node) => ({
          node,
          vclN: String(node.vehicleNo || '').trim(),
          vco: resolveGarageVehicleColor(node)
        })).filter((v) => v.vclN)
        if (!vehicles.length) return
        this.$emit('vehicle-check-change', { checked, vehicles, fromGroup: true })
      } finally {
        setTimeout(() => {
          this.suppressGarageTreeCheckChange = false
        }, 50)
      }
    },
    onGarageTreeWrapClick(e) {
      try {
        const target = e && e.target ? e.target : null
        const hitNodeContent = target && target.closest ? target.closest('.el-tree-node__content') : null
        if (hitNodeContent) return
      } catch (err) {
        /* ignore */
      }
      this.selectedGarageNode = null
      const tree = this.$refs.garageVehicleTree
      if (tree && typeof tree.setCurrentKey === 'function') {
        try {
          tree.setCurrentKey(null)
        } catch (err2) {
          /* ignore */
        }
      }
    },
    async loadGarageTreeData() {
      if (!this.enableTreeFetch || !this.candidateScope) return
      const sequence = ++this.requestSequence
      const actor = authState.user
      const query = this.garageVehicleSearchText.trim()
      const isCurrent = () => sequence === this.requestSequence && actor === authState.user && query === this.garageVehicleSearchText.trim()
      try {
        const rows = await loadNormalVehicleGarageTree(query)
        if (!isCurrent()) return
        this.vehicleTreeData = rows
        this.syncGarageSearchExpandState()
        this.$nextTick(() => { if (isCurrent()) this.$emit('tree-loaded') })
      } catch (error) {
        if (!isCurrent()) return
        this.$message.error(error.msg || error.message || '车辆分组加载失败')
      }
    },
    /** 拉取某分组下子节点原始列表（分页） */
    async fetchGarageChildrenRaw(parentId, page = 1) {
      const parent = findGarageNodeById(this.vehicleTreeData, parentId)
      const start = (Math.max(1, Number(page) || 1) - 1) * 500
      return parent ? parent.children.slice(start, start + 500) : []
    },
    async loadGarageChildren(parentId, expectedTotal = null) {
      const list = await this.fetchGarageChildrenRaw(parentId, 1)
      const normalized = await Promise.all(list.map((item) => this.normalizeGarageNode(item)))
      if (shouldShowGarageLoadMore(expectedTotal, list.length, normalized.length)) {
        normalized.push(createGarageLoadMoreNode(parentId, 2))
      }
      return normalized
    },
    async handleGarageLoadMoreClick(data) {
      if (this.disabled || this.garageLoadMoreLoading) return
      const parentId = data && data.garageLoadMoreParentId
      const page = data && data.garageChildrenNextPage
      if (parentId == null || page == null) return
      const p = Number(page)
      if (!Number.isFinite(p) || p < 2) return
      this.garageLoadMoreLoading = true
      try {
        const parentNode = this.findNodeById(this.vehicleTreeData, parentId)
        if (!parentNode) return
        const prev = Array.isArray(parentNode.children) ? parentNode.children : []
        const withoutMore = prev.filter((c) => c && c.type !== GARAGE_NODE_LOAD_MORE)
        const list = await this.fetchGarageChildrenRaw(parentId, p)
        if (!list.length) {
          this.$set(parentNode, 'children', withoutMore)
          return
        }
        const newNormalized = await Promise.all(list.map((item) => this.normalizeGarageNode(item)))
        const seen = new Set(withoutMore.map((c) => String(c.id)))
        for (const nn of newNormalized) {
          if (nn && !seen.has(String(nn.id))) {
            withoutMore.push(nn)
            seen.add(String(nn.id))
          }
        }
        const merged = withoutMore
        const totalExp = parentNode.garageExpectedChildTotal
        if (shouldShowGarageLoadMore(totalExp, list.length, merged.length)) {
          merged.push(createGarageLoadMoreNode(parentId, p + 1))
        }
        this.$set(parentNode, 'children', merged)
      } catch (e) {
        console.error('加载下一页失败', e)
        this.$message.error('加载下一页失败')
      } finally {
        this.garageLoadMoreLoading = false
      }
    },
    renderGarageTreeContent(h, { node, data }) {
      if (data && data.type === GARAGE_NODE_LOAD_MORE) {
        return h('span', { class: 'garage-tree-load-more' }, data.label || '下一页')
      }
      const isVehicle = data && data.vehicleNo != null
      const isGroup = !isVehicle && isGarageGroupNode(data)
      const children = []
      if (isGroup) {
        children.push(h('i', { class: 'garage-tree-icon garage-tree-icon--folder el-icon-folder' }))
        const name = data.displayName || data.teamName || data.name || data.label || ''
        const vc = data.vehicleCount != null ? Number(data.vehicleCount) : null
        const tvc = data.totalVehicleCount != null ? Number(data.totalVehicleCount) : null
        let countLabel = ''
        if (vc != null && tvc != null && Number.isFinite(vc) && Number.isFinite(tvc)) {
          countLabel = `${vc}/${tvc}`
        } else {
          const count = tvc != null && Number.isFinite(tvc) ? tvc : vc != null && Number.isFinite(vc) ? vc : null
          if (count != null) countLabel = String(count)
        }
        children.push(h('span', { class: 'garage-tree-group-name' }, name))
        if (countLabel) children.push(h('span', { class: 'garage-tree-group-count' }, ` [${countLabel}]`))
      } else if (isVehicle) {
        children.push(renderGarageVehicleTreeIcon(h))
        children.push(h('span', { class: 'garage-tree-plate' }, String(data.vehicleNo || '')))
        const plate = data.vehicleNo != null ? String(data.vehicleNo).trim() : ''
        const suffix = plate && this.vehicleStatusMap ? this.vehicleStatusMap[`${plate}_${resolveGarageVehicleColor(data)}`] : ''
        if (suffix) {
          children.push(h('span', { class: 'garage-tree-status' }, ` ${suffix}`))
        }
      } else {
        children.push(h('span', { class: 'garage-tree-node-label' }, node.label))
      }
      return h('span', { class: 'garage-tree-node' }, children)
    },
    async normalizeGarageNode(item) {
      const raw = item && typeof item === 'object' ? item : {}
      const idRaw = raw.id != null ? raw.id : raw.teamId != null ? raw.teamId : raw.vehicleId
      const id = idRaw != null ? String(idRaw) : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const type = raw.type != null ? String(raw.type).toUpperCase() : ''
      const isVehicle = type === 'VEHICLE' || raw.vehicleNo != null
      const name =
        raw.title != null
          ? String(raw.title)
          : isVehicle
            ? String(raw.vehicleNo || raw.vclN || id)
            : String(raw.teamName || raw.name || id)
      const label = isVehicle ? name : buildGroupLabel(name, raw)

      const node = {
        ...raw,
        id,
        label,
        displayName: name,
        type: isVehicle ? 'VEHICLE' : 'GROUP',
        children: []
      }

      if (isVehicle) {
        node.isLeaf = true
        return node
      }

      if (Array.isArray(raw.children) && raw.children.length) {
        node.children = await Promise.all(raw.children.map((c) => this.normalizeGarageNode(c)))
        return node
      }

      if (raw.hasChildren === true || raw.hasChildren === 1) {
        try {
          node.garageExpectedChildTotal = resolveGarageGroupExpectedChildTotal(raw)
          node.children = await this.loadGarageChildren(id, node.garageExpectedChildTotal)
        } catch (e) {
          node.children = []
        }
      }
      return node
    },
    syncGarageSearchExpandState() {
      this.$nextTick(() => {
        applyElTreeExpandAll(this.$refs.garageVehicleTree, true)
      })
    },
    findNodeById(nodes, id) {
      return findGarageNodeById(nodes, id)
    },
    getVehicleTreeData() {
      return Array.isArray(this.vehicleTreeData) ? this.vehicleTreeData : []
    },
    findVehicleNodeIdByPlate(plate) {
      const target = plate != null ? String(plate).trim() : ''
      if (!target) return null
      const walk = (nodes) => {
        for (const n of nodes || []) {
          if (!n) continue
          if (n.vehicleNo != null && String(n.vehicleNo).trim() === target) return n.id
          if (Array.isArray(n.children) && n.children.length) {
            const found = walk(n.children)
            if (found != null) return found
          }
        }
        return null
      }
      return walk(this.vehicleTreeData)
    },
    setVehiclePlatesChecked(plates, checked) {
      const tree = this.$refs.garageVehicleTree
      if (!tree || typeof tree.setChecked !== 'function') return
      const list = Array.isArray(plates) ? plates : [plates]
      const uniq = Array.from(new Set(list.map((p) => String(p || '').trim()).filter(Boolean)))
      if (!uniq.length) return
      this.suppressGarageTreeCheckChange = true
      try {
        uniq.forEach((plate) => {
          const nodeId = this.findVehicleNodeIdByPlate(plate)
          if (nodeId != null) tree.setChecked(nodeId, !!checked, false)
        })
      } finally {
        setTimeout(() => {
          this.suppressGarageTreeCheckChange = false
        }, 0)
      }
    },
    refreshTreeDisplay() {
      this.$nextTick(() => {
        const tree = this.$refs.garageVehicleTree
        if (tree && typeof tree.$forceUpdate === 'function') tree.$forceUpdate()
      })
    },
    /** 分组全选：拉齐该组及子组下全部分页车辆 */
    async ensureGarageGroupChildrenFullyLoaded(node) {
      if (!node || typeof node !== 'object') return
      const pid = String(node.id)
      let expectedTotal =
        node.garageExpectedChildTotal != null && Number.isFinite(node.garageExpectedChildTotal)
          ? node.garageExpectedChildTotal
          : resolveGarageGroupExpectedChildTotal(node)
      if (node.garageExpectedChildTotal == null && expectedTotal != null) {
        this.$set(node, 'garageExpectedChildTotal', expectedTotal)
      }
      const merged = []
      const seen = new Set()
      let page = 1
      for (;;) {
        const list = await this.fetchGarageChildrenRaw(pid, page)
        if (!list.length) break
        const batch = await Promise.all(list.map((item) => this.normalizeGarageNode(item)))
        for (const nn of batch) {
          if (nn && !seen.has(String(nn.id))) {
            merged.push(nn)
            seen.add(String(nn.id))
          }
        }
        const pageLen = list.length
        if (!shouldShowGarageLoadMore(expectedTotal, pageLen, merged.length)) break
        page += 1
        if (page > 500) break
      }
      this.$set(node, 'children', merged)
    },
    async ensureGarageGroupDescendantsLoaded(node) {
      if (!node || typeof node !== 'object') return
      if (node.vehicleNo != null) return
      const hasMore = node.hasChildren === true || node.hasChildren === 1
      const prev = Array.isArray(node.children) ? node.children : []
      const hasLoadMore = prev.some((c) => c && c.type === GARAGE_NODE_LOAD_MORE)
      if (hasMore && (prev.length === 0 || hasLoadMore)) {
        try {
          await this.ensureGarageGroupChildrenFullyLoaded(node)
        } catch (e) {
          this.$set(node, 'children', [])
        }
      }
      const children = Array.isArray(node.children) ? node.children : []
      for (const c of children) {
        if (!c || c.type === GARAGE_NODE_LOAD_MORE) continue
        if (c.vehicleNo == null && isGarageGroupNode(c)) {
          // eslint-disable-next-line no-await-in-loop
          await this.ensureGarageGroupDescendantsLoaded(c)
        }
      }
    }
  }
}
</script>

<style scoped>
.lj-garage-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}
.garage-toolbar {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
  flex-shrink: 0;
}
.lj-garage-search {
  flex: 1;
  min-width: 0;
}
.vehicle-tree-wrap {
  flex: 1;
  min-height: 10rem;
  max-height: 18rem;
  overflow: auto;
  border: 0.0625rem solid #ebeef5;
  border-radius: 0.25rem;
  padding: 0.25rem;
}
.vehicle-tree {
  font-size: 0.875rem;
  border-radius: 0.5rem;
}
.vehicle-tree >>> .el-tree-node.is-current > .el-tree-node__content {
  background-color: var(--layout-primary-soft, rgba(8, 125, 255, 0.08)) !important;
}
.vehicle-tree >>> .garage-tree-load-more {
  color: var(--layout-primary, #087dff);
  cursor: pointer;
  user-select: none;
}
.vehicle-tree >>> .el-tree-node__content {
  height: 1.625rem;
  line-height: 1.625rem;
}
.vehicle-tree >>> .el-tree-node__expand-icon {
  color: var(--layout-primary, #087dff);
  font-size: 0.75rem;
  margin-right: 0.125rem;
}
.vehicle-tree >>> .el-tree-node__expand-icon.is-leaf {
  color: transparent;
}
.vehicle-tree >>> .el-tree-node__children::before {
  content: '';
  position: absolute;
  left: 0.4375rem;
  top: 0;
  bottom: 0.5rem;
  border-left: 1px dotted #c0c4cc;
  pointer-events: none;
}
.vehicle-tree >>> .el-tree-node__children {
  position: relative;
}
.vehicle-tree >>> .el-checkbox {
  margin-right: 0.375rem;
}
.vehicle-tree >>> .el-checkbox__inner:hover,
.vehicle-tree >>> .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: var(--layout-primary, #087dff);
}
.vehicle-tree >>> .el-checkbox__input.is-checked .el-checkbox__inner,
.vehicle-tree >>> .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: var(--layout-primary, #087dff);
  border-color: var(--layout-primary, #087dff);
}
.vehicle-tree >>> .el-checkbox__input.is-checked .el-checkbox__inner:hover,
.vehicle-tree >>> .el-checkbox__input.is-indeterminate .el-checkbox__inner:hover {
  background-color: var(--layout-primary-hover, #0670e6);
  border-color: var(--layout-primary-hover, #0670e6);
}
.vehicle-tree >>> .garage-tree-node {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.vehicle-tree >>> .garage-tree-icon--folder {
  color: #e6a23c;
  margin-right: 0.25rem;
}
.vehicle-tree >>> .garage-tree-vehicle-icon {
  width: 1.125rem;
  height: 1.125rem;
  min-width: 1.125rem;
  margin-right: 0.25rem;
  flex-shrink: 0;
  object-fit: contain;
  vertical-align: middle;
}
.vehicle-tree >>> .garage-tree-group-count {
  color: #909399;
}
.vehicle-tree >>> .garage-tree-status {
  color: #909399;
  font-size: 0.8125rem;
}
.lj-garage-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: var(--layout-primary, #087dff);
  cursor: pointer;
  flex-shrink: 0;
}
.lj-garage-icon-btn:hover:not(:disabled) {
  background: var(--layout-primary-soft, rgba(8, 125, 255, 0.08));
}
.lj-garage-icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
